import { Bookmark, BookmarkPlus, Check, Link2, Trash2, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from './Button.tsx';
import { Checkbox } from './Checkbox.tsx';
import { Dialog } from './Dialog.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './DropdownMenu.tsx';
import { Input } from './Input.tsx';

export interface SavedViewItem {
  id: string;
  name: string;
  /** 'team' views are shared through the hub; 'mine' live in this browser. */
  shared?: boolean;
  /** One line describing the filters, for the menu. */
  summary?: string;
}

export interface SavedViewsMenuProps {
  views: SavedViewItem[];
  /** The view the page currently shows, when it matches one. */
  currentId?: string | null;
  /** Shown on the trigger when the address names a view nobody here saved (a shared link). */
  linkedName?: string | null;
  /** Whether the page has any filter, sort or grouping worth saving. */
  canSave: boolean;
  onApply: (id: string) => void;
  onSave: (name: string, opts: { shared: boolean }) => void | Promise<void>;
  onDelete?: (id: string) => void;
  onCopyLink: () => void;
  /** Offer "share with the team" when the hub supports it. */
  canShare?: boolean;
}

/** Saved views: filters, sort and grouping kept under a name, opened in one click, shared by link. */
export function SavedViewsMenu({ views, currentId, linkedName, canSave, onApply, onSave, onDelete, onCopyLink, canShare }: SavedViewsMenuProps) {
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const current = views.find((v) => v.id === currentId);
  const label = current?.name ?? linkedName ?? 'Views';

  useEffect(() => {
    if (naming) {
      setName(linkedName ?? '');
      setShared(false);
    }
  }, [naming, linkedName]);

  const save = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      await onSave(n, { shared });
      setNaming(false);
    } finally {
      setBusy(false);
    }
  };

  const mine = views.filter((v) => !v.shared);
  const team = views.filter((v) => v.shared);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant={current || linkedName ? 'secondary' : 'ghost'} icon={<Bookmark />} aria-label={`Saved views${current ? `, showing ${current.name}` : ''}`}>
            <span className="max-w-40 truncate">{label}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          {views.length === 0 && (
            <p className="p-2 text-ui text-fg-muted">No saved views yet. Filter the list, then save it here to open it in one click.</p>
          )}
          {mine.length > 0 && <DropdownMenuLabel>Your views</DropdownMenuLabel>}
          {mine.map((v) => (
            <DropdownMenuItem key={v.id} icon={v.id === currentId ? <Check /> : <Bookmark />} onSelect={() => onApply(v.id)}>
              <span className="flex flex-col">
                <span className="truncate">{v.name}</span>
                {v.summary && <span className="truncate text-caption text-fg-subtle">{v.summary}</span>}
              </span>
            </DropdownMenuItem>
          ))}
          {team.length > 0 && <DropdownMenuLabel>Team views</DropdownMenuLabel>}
          {team.map((v) => (
            <DropdownMenuItem key={v.id} icon={v.id === currentId ? <Check /> : <Users />} onSelect={() => onApply(v.id)}>
              <span className="flex flex-col">
                <span className="truncate">{v.name}</span>
                {v.summary && <span className="truncate text-caption text-fg-subtle">{v.summary}</span>}
              </span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem icon={<BookmarkPlus />} disabled={!canSave} onSelect={() => setNaming(true)}>
            {canSave ? 'Save this view' : 'Filter the list to save a view'}
          </DropdownMenuItem>
          <DropdownMenuItem icon={<Link2 />} onSelect={onCopyLink}>
            Copy link to this view
          </DropdownMenuItem>
          {current && onDelete && (
            <DropdownMenuItem icon={<Trash2 />} danger onSelect={() => onDelete(current.id)}>
              Delete "{current.name}"
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog
        open={naming}
        onOpenChange={setNaming}
        title="Save this view"
        description="Its filters, sort and grouping open again in one click, from this menu and from the command palette."
        size="sm"
        initialFocus={() => inputRef.current}
        footer={
          <>
            <Button variant="ghost" onClick={() => setNaming(false)}>
              Cancel
            </Button>
            <Button variant="primary" loading={busy} disabled={!name.trim()} onClick={() => void save()}>
              Save view
            </Button>
          </>
        }
      >
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Input ref={inputRef} label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Failed today on build-01" maxLength={60} />
          {canShare && <Checkbox checked={shared} onCheckedChange={setShared} label="Share with everyone on this hub" />}
        </form>
      </Dialog>
    </>
  );
}
